import { useState } from 'react';
import { Button, Callout, Checkbox, FileUpload, Modal } from '@humanforce/mango-ui-react';
import type { PayRunPopulationRefreshAccepted, TransactionImportError } from './payRunPopulationApiTypes';
import { loadPopulation, loadTransactions, PayRunPopulationRequestError } from './payRunPopulationHttpClient';

interface PayRunDataLoadModalProps {
  payId: string;
  visible: boolean;
  onHide: () => void;
  onAccepted: (accepted: PayRunPopulationRefreshAccepted) => void;
}

const maximumEmployeeFileSize = 64 * 1024 * 1024;
const maximumTimesheetFileSize = 64 * 1024 * 1024;

export const PayRunDataLoadModal = ({
  payId,
  visible,
  onHide,
  onAccepted,
}: PayRunDataLoadModalProps) => {
  const [loadFromHrApi, setLoadFromHrApi] = useState(false);
  const [loadFromWfmApi, setLoadFromWfmApi] = useState(false);
  const [clearExistingData, setClearExistingData] = useState(true);
  const [employeeFiles, setEmployeeFiles] = useState<File[]>([]);
  const [timesheetFiles, setTimesheetFiles] = useState<File[]>([]);
  const [employeeError, setEmployeeError] = useState<string | null>(null);
  const [timesheetError, setTimesheetError] = useState<string | null>(null);
  const [transactionErrors, setTransactionErrors] = useState<TransactionImportError[]>([]);
  const [populationFieldErrors, setPopulationFieldErrors] = useState<string[]>([]);
  const [employeeLoaded, setEmployeeLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setLoadFromHrApi(false);
    setLoadFromWfmApi(false);
    setClearExistingData(true);
    setEmployeeFiles([]);
    setTimesheetFiles([]);
    setEmployeeError(null);
    setTimesheetError(null);
    setTransactionErrors([]);
    setPopulationFieldErrors([]);
    setEmployeeLoaded(false);
    setError(null);
    setSubmitting(false);
  };

  const resetAndHide = () => {
    if (submitting) return;
    reset();
    onHide();
  };

  const submit = async () => {
    const employeeFile = employeeFiles[0];
    const timesheetFile = timesheetFiles[0];
    const employeeMissing = !employeeLoaded && !loadFromHrApi && !employeeFile;
    const timesheetMissing = !loadFromWfmApi && !timesheetFile;
    if (employeeMissing || timesheetMissing) {
      setEmployeeError(employeeMissing ? 'Select an Employees JSON file or load from the HR API.' : null);
      setTimesheetError(timesheetMissing ? 'Select a Timesheet records JSON file or load from the WFM API.' : null);
      setTransactionErrors([]);
      return;
    }
    setSubmitting(true);
    setEmployeeError(null);
    setTimesheetError(null);
    setTransactionErrors([]);
    setPopulationFieldErrors([]);
    setError(null);
    try {
      if (!employeeLoaded) {
        const accepted = await loadPopulation(payId, {
          employeeSource: loadFromHrApi ? 'hr_api' : 'json_upload',
          employeeFile: loadFromHrApi ? undefined : employeeFile,
          clearExistingData,
        });
        onAccepted(accepted);
        setEmployeeLoaded(true);
      }
      const transactions = await loadTransactions(payId, {
        transactionSource: loadFromWfmApi ? 'wfm_api' : 'json_upload',
        timesheetFile: loadFromWfmApi ? undefined : timesheetFile,
      });
      if (transactions.status === 'failed' || transactions.status === 'review_required') {
        setTransactionErrors(transactions.errors);
        setError(transactions.status === 'review_required'
          ? `${transactions.reviewRequiredIssueCount} employee transaction result${transactions.reviewRequiredIssueCount === 1 ? '' : 's'} require review.`
          : 'The transaction file could not be loaded. Correct the reasons below and try again.');
        setSubmitting(false);
        return;
      }
      reset();
      onHide();
    } catch (loadError) {
      setPopulationFieldErrors(loadError instanceof PayRunPopulationRequestError
        ? loadError.fieldErrors.map(fieldError => `${fieldError.field}: ${fieldError.message ?? fieldError.code}`)
        : []);
      setError(loadError instanceof Error ? loadError.message : 'Pay Run data could not be loaded.');
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      header="Load Pay Run data"
      size="large"
      closable={!submitting}
      dismissableMask={!submitting}
      closeOnEscape={!submitting}
      onHide={resetAndHide}
      footer={(
        <span className="mgo-cluster mgo-justify-end mgo-gap-s">
          <Button label="Cancel" variant="secondary" disabled={submitting} onClick={resetAndHide} />
          <Button label="Load data" loading={submitting} onClick={() => void submit()} />
        </span>
      )}
    >
      <div className="mgo-stack mgo-gap-l">
        <Checkbox
          id="clear-existing-pay-run-data"
          label="Clear existing employee and transaction data before loading"
          helpText="If loading fails, this Pay Run will remain empty so you can correct the source and try again."
          checked={clearExistingData}
          disabled={submitting || employeeLoaded}
          onChange={setClearExistingData}
        />
        <section aria-labelledby="employees-source-heading" className="mgo-stack mgo-gap-m">
          <h3 id="employees-source-heading" className="mgo-heading-s">Employees</h3>
          <Checkbox
            id="load-from-hr-api"
            label="Load from HR API"
            checked={loadFromHrApi}
            disabled={employeeLoaded || submitting}
            onChange={(checked) => {
              setLoadFromHrApi(checked);
              setEmployeeError(null);
              if (checked) setEmployeeFiles([]);
            }}
          />
          <FileUpload
            label="Employees JSON file"
            files={employeeFiles}
            onFilesChange={(files) => { setEmployeeFiles(files.slice(0, 1)); setEmployeeError(null); }}
            accept=".json,application/json"
            maxFileSize={maximumEmployeeFileSize}
            required={!loadFromHrApi}
            disabled={employeeLoaded || loadFromHrApi || submitting}
            invalid={employeeError != null}
            helpText={employeeLoaded
              ? 'Employees were loaded successfully. Correct or replace only the transaction file; a retry will not reload employees.'
              : loadFromHrApi
                ? 'The configured HR API source will be used.'
                : 'Select one reusable employee population JSON file. The selected Pay Run supplies its Pay Group and period.'}
            status={employeeError}
          />
        </section>

        <section aria-labelledby="timesheet-source-heading" className="mgo-stack mgo-gap-m">
          <h3 id="timesheet-source-heading" className="mgo-heading-s">Timesheet records</h3>
          <Checkbox
            id="load-from-wfm-api"
            label="Load from WFM API"
            checked={loadFromWfmApi}
            disabled={submitting}
            onChange={(checked) => {
              setLoadFromWfmApi(checked);
              setTimesheetError(null);
              setTransactionErrors([]);
              if (checked) setTimesheetFiles([]);
            }}
          />
          <FileUpload
            label="Timesheet records JSON file"
            files={timesheetFiles}
            onFilesChange={(files) => { setTimesheetFiles(files.slice(0, 1)); setTimesheetError(null); setTransactionErrors([]); }}
            accept=".json,application/json"
            maxFileSize={maximumTimesheetFileSize}
            required={!loadFromWfmApi}
            disabled={loadFromWfmApi || submitting}
            invalid={timesheetError != null}
            helpText={loadFromWfmApi
              ? 'The configured WFM API source will be used. It currently fails closed until configured.'
              : 'Select one reusable transaction JSON file. Include Person ID and transaction facts only; the selected Pay Run supplies Pay Group and period.'}
            status={timesheetError}
          />
          <Callout tone="info" title="Pay Run values are supplied automatically">
            The file only needs source transaction details and each employee&apos;s Person ID. Pay Group, Business Record, Pay Run, period and population values come from this Pay Run.
          </Callout>
        </section>

        {error && (
          <Callout tone="critical" title={employeeLoaded ? 'Employees loaded; transactions not loaded' : 'Pay Run data not loaded'}>
            <div className="mgo-stack mgo-gap-s">
              <span>{error}</span>
              {transactionErrors.length > 0 && (
                <ul className="mgo-stack mgo-gap-xs">
                  {transactionErrors.map((transactionError, index) => (
                    <li key={`${transactionError.code}-${transactionError.field}-${index}`}>
                      <strong>{transactionError.field}</strong>
                      {transactionError.sourceRecordId ? ` (${transactionError.sourceRecordId})` : ''}: {transactionError.reason}
                    </li>
                  ))}
                </ul>
              )}
              {populationFieldErrors.length > 0 && (
                <ul className="mgo-stack mgo-gap-xs">
                  {populationFieldErrors.map(fieldError => <li key={fieldError}>{fieldError}</li>)}
                </ul>
              )}
            </div>
          </Callout>
        )}
      </div>
    </Modal>
  );
};
